import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PencilLine, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function MarksEntry() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [examId, setExamId] = useState("");
  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({}); // { studentId: { subjectId: {obtained, absent} } }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEnter = hasPerm(user, "enter_marks");

  useEffect(() => {
    api.get("/exams").then((r) => setExams(r.data)).catch(() => {});
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  const shownExams = classId ? exams.filter((e) => e.class_id === classId) : exams;

  useEffect(() => {
    if (!examId) { setExam(null); setStudents([]); setMarks({}); return; }
    setLoading(true);
    const ex = exams.find((e) => e.id === examId);
    Promise.all([
      api.get(`/exams/${examId}`),
      api.get(`/students`, { params: { class_id: ex?.class_id, limit: 1000 } }),
      api.get(`/exams/${examId}/marks`),
    ]).then(([exRes, stuRes, mkRes]) => {
      setExam(exRes.data);
      setStudents(stuRes.data.students || []);
      setMarks(mkRes.data || {});
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [examId]); // eslint-disable-line

  const setMark = (sid, subId, patch) => {
    setMarks((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [subId]: { ...((p[sid] || {})[subId] || {}), ...patch } } }));
  };

  const save = async () => {
    setSaving(true);
    const entries = [];
    students.forEach((s) => {
      (exam.subjects || []).forEach((sub) => {
        const cell = (marks[s.id] || {})[sub.subject_id] || {};
        entries.push({ student_id: s.id, subject_id: sub.subject_id, obtained: Number(cell.obtained) || 0, absent: !!cell.absent });
      });
    });
    try { await api.post(`/exams/${examId}/marks`, { entries }); toast.success("Marks saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><PencilLine className="text-indigo-500" size={26} />Marks Entry</h1>
        <p className="text-slate-500">Select an exam, enter marks per subject, then save.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <Select value={classId} onValueChange={(v) => { setClassId(v); setExamId(""); }}>
          <SelectTrigger className="w-56" data-testid="marks-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={examId} onValueChange={setExamId}>
          <SelectTrigger className="w-80" data-testid="marks-exam-select"><SelectValue placeholder="Select an exam" /></SelectTrigger>
          <SelectContent>{shownExams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.class_name} · {e.term}</SelectItem>)}</SelectContent>
        </Select>
        {exam && canEnter && <Button onClick={save} disabled={saving} className="gap-1.5 ml-auto" data-testid="save-marks-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Marks
        </Button>}
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
        !exam ? <div className="py-16 text-center text-slate-400">Choose an exam to begin.</div> :
        students.length === 0 ? <div className="py-16 text-center text-slate-400">No students in this class.</div> : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[calc(100vh-320px)]">
            <table className="text-sm border-collapse min-w-max">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 border border-slate-200 bg-slate-50 sticky left-0 z-20 text-left">Roll</th>
                  <th className="px-3 py-2 border border-slate-200 bg-slate-50 sticky left-14 z-20 text-left min-w-[160px]">Student</th>
                  {exam.subjects.map((sub) => (
                    <th key={sub.subject_id} className="px-3 py-2 border border-slate-200 bg-slate-50 text-center min-w-[110px]">
                      {sub.subject_name}<div className="text-[10px] font-normal normal-case text-slate-400">/ {sub.max_marks}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} data-testid={`marks-row-${s.admission_number}`}>
                    <td className="px-3 py-1.5 border border-slate-200 bg-white text-slate-500 sticky left-0 z-10">{s.roll_number || "—"}</td>
                    <td className="px-3 py-1.5 border border-slate-200 bg-white font-medium text-slate-800 sticky left-14 z-10">{s.name}</td>
                    {exam.subjects.map((sub) => {
                      const cell = (marks[s.id] || {})[sub.subject_id] || {};
                      return (
                        <td key={sub.subject_id} className="px-2 py-1 border border-slate-200">
                          <div className="flex items-center gap-1.5">
                            <input type="number" min="0" max={sub.max_marks} disabled={!canEnter || cell.absent}
                              value={cell.absent ? "" : (cell.obtained ?? "")}
                              onChange={(e) => setMark(s.id, sub.subject_id, { obtained: e.target.value })}
                              className="w-14 h-8 px-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400 disabled:bg-slate-100"
                              data-testid={`mark-${s.admission_number}-${sub.subject_id}`} />
                            <label className="flex items-center gap-1 text-[10px] text-slate-400" title="Absent">
                              <Checkbox checked={!!cell.absent} disabled={!canEnter} onCheckedChange={(v) => setMark(s.id, sub.subject_id, { absent: !!v })} className="h-3.5 w-3.5" />AB
                            </label>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
